import { useState, useEffect, useCallback } from 'react';
import API from '../Api';

// In-memory cache for categories
let categoriesCache = null;
let categoriesCacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Custom hook to fetch and cache active categories
 * Prevents duplicate API calls across components
 * @returns {Object} { categories, loading, error, refetch }
 */
const useCategories = () => {
	const [categories, setCategories] = useState(categoriesCache || []);
	const [loading, setLoading] = useState(!categoriesCache);
	const [error, setError] = useState(null);

	const fetchCategories = useCallback(async () => {
		// Check if cache is still valid
		if (categoriesCache && categoriesCacheTime) {
			const now = Date.now();
			if (now - categoriesCacheTime < CACHE_DURATION) {
				setCategories(categoriesCache);
				setLoading(false);
				return categoriesCache;
			}
		}

		setLoading(true);
		setError(null);

		try {
			const res = await API.get('category/active-category');
			const categoriesData = res.data.data || [];

			// Update cache
			categoriesCache = categoriesData;
			categoriesCacheTime = Date.now();

			setCategories(categoriesData);
			setLoading(false);
			return categoriesData;
		} catch (err) {
			setError(err);
			setLoading(false);

			// Still return cached data if available on error
			if (categoriesCache) {
				setCategories(categoriesCache);
				return categoriesCache;
			}
			return [];
		}
	}, []);

	useEffect(() => {
		fetchCategories();
	}, [fetchCategories]);

	// Manual refetch function to invalidate cache
	const refetch = useCallback(async () => {
		categoriesCache = null;
		categoriesCacheTime = null;
		return await fetchCategories();
	}, [fetchCategories]);

	return {
		categories,
		loading,
		error,
		refetch
	};
};

// Export function to clear cache (useful for testing or manual invalidation)
export const clearCategoriesCache = () => {
	categoriesCache = null;
	categoriesCacheTime = null;
	
};

export default useCategories;
